import React from "react";
import { useTranslation } from "react-i18next";
import { FaCheckCircle, FaExclamationCircle, FaMinusCircle } from "react-icons/fa";
import U2000BasicDialog from "../../../u2000Core/components/dialog/U2000BasicDialog";
import U2000ActionBtn from "../../../u2000Core/components/buttons/U2000ActionBtn";
import { u2000FormatIntegerCount } from "../../../u2000Core/tools/U2000NumberFormat";
import { NS } from "../../U3130";
import type { U3130ApproveBatchResult } from "../../U3130Service";

interface U3130ApproveBatchDialogProps {
  open: boolean;
  preview: U3130ApproveBatchResult | null;
  isExecuting: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

function pluralKey(base: string, count: number): string {
  return count === 1 ? base : `${base}_other`;
}

interface U3130ApproveBatchStatRowProps {
  count: number;
  labelKey: string;
  icon: React.ReactNode;
  countClassName: string;
  locale: string;
  t: (key: string, opts?: { ns: string; count?: number }) => string;
}

const U3130ApproveBatchStatRow: React.FC<U3130ApproveBatchStatRowProps> = ({
  count,
  labelKey,
  icon,
  countClassName,
  locale,
  t,
}) => (
  <div className="flex items-center gap-3 px-4 py-3">
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white shadow-xs ring-1 ring-gray-200/80" aria-hidden>
      {icon}
    </span>
    <span className="min-w-0 flex-1 text-sm text-gray-800">{t(pluralKey(labelKey, count), { ns: NS, count })}</span>
    <span className={`shrink-0 text-xl font-semibold tabular-nums ${countClassName}`}>
      {u2000FormatIntegerCount(count, locale)}
    </span>
  </div>
);

const U3130ApproveBatchDialog: React.FC<U3130ApproveBatchDialogProps> = ({
  open,
  preview,
  isExecuting,
  onConfirm,
  onClose,
}) => {
  const { t, i18n } = useTranslation();
  const approvedCount = preview?.approvedCount ?? 0;
  const skippedCount = preview?.skippedCount ?? 0;
  const errorCount = preview?.errorCount ?? 0;
  const canConfirm = approvedCount > 0 && !isExecuting;
  const locale = i18n.language;

  return (
    <U2000BasicDialog
      isOpen={open}
      onClose={() => {
        if (!isExecuting) onClose();
      }}
      title={t("dlgApproveBatchTitle", { ns: NS })}
      width="medium"
    >
      <div className="flex flex-col gap-4 px-6 py-4">
        {!preview && !isExecuting && (
          <p className="text-sm text-gray-500" role="status">
            {t("dlgApproveBatchLoadingPreview", { ns: NS })}
          </p>
        )}

        {preview && (
          <>
            <p className="text-sm leading-relaxed text-gray-700">{t("dlgApproveBatchIntro", { ns: NS })}</p>

            <div
              className="overflow-hidden rounded-lg border border-gray-200 bg-gray-50/90 divide-y divide-gray-200"
              role="region"
              aria-label={t("dlgApproveBatchTitle", { ns: NS })}
            >
              <U3130ApproveBatchStatRow
                count={approvedCount}
                labelKey="dlgApproveBatchStatApproved"
                icon={<FaCheckCircle className="h-4 w-4 text-emerald-600" />}
                countClassName="text-emerald-700"
                locale={locale}
                t={t}
              />
              <U3130ApproveBatchStatRow
                count={skippedCount}
                labelKey="dlgApproveBatchStatSkipped"
                icon={<FaMinusCircle className="h-4 w-4 text-amber-600" />}
                countClassName="text-amber-800"
                locale={locale}
                t={t}
              />
              <U3130ApproveBatchStatRow
                count={errorCount}
                labelKey="dlgApproveBatchStatErrors"
                icon={<FaExclamationCircle className="h-4 w-4 text-red-600" />}
                countClassName={errorCount > 0 ? "text-red-700" : "text-gray-500"}
                locale={locale}
                t={t}
              />
            </div>

            <p className="text-xs leading-snug text-gray-500">{t("dlgApproveBatchSkippedHint", { ns: NS })}</p>

            {errorCount > 0 && (
              <div
                className="rounded-lg border border-red-200 bg-red-50/80 px-3 py-2 text-xs leading-snug text-red-900"
                role="note"
              >
                {t("dlgApproveBatchPreviewErrorsHint", { ns: NS })}
              </div>
            )}

            {approvedCount === 0 && !isExecuting && (
              <div
                className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm leading-snug text-amber-950"
                role="alert"
              >
                {t("dlgApproveBatchNoEligible", { ns: NS })}
              </div>
            )}
          </>
        )}

        {isExecuting && (
          <p className="text-sm font-medium text-blue-800" role="status">
            {t("dlgApproveBatchExecuting", { ns: NS })}
          </p>
        )}

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-2">
          <U2000ActionBtn
            variant="secondary"
            label={t("btnCancel", { ns: "common" })}
            handleClick={onClose}
            disabled={isExecuting}
          />
          <U2000ActionBtn
            label={t("btnApproveBatchConfirm", { ns: NS })}
            handleClick={onConfirm}
            disabled={!canConfirm}
          />
        </div>
      </div>
    </U2000BasicDialog>
  );
};

export default U3130ApproveBatchDialog;
